import type Owner from '@ember/owner';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { ActivityLogObjectType } from 'webapp/enums/activity-log';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getPrettyModelName } from 'webapp/utils/model';

import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import AccountLogsComponent from 'webapp/components/account/logs/index';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import Alert from 'webapp/models-custom/alert';
import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import BoardShare from 'webapp/models-custom/board-share';
import CustomQuery from 'webapp/models-custom/custom-query';
import Goal from 'webapp/models-custom/goal';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import Report from 'webapp/models-custom/report';
import Script from 'webapp/models-custom/script';
import Source from 'webapp/models-custom/source';
import Team from 'webapp/models-custom/team';

function isPopupDataForMetricRegistry(
	popupData: PopupData.ActivityLog.Preview,
): popupData is PopupData.ActivityLog.MetricRegistryPreview {
	return 'metricRegistryItem' in popupData;
}

export default class AccountPopupInactivityComponent extends PopupInnerModalComponent<PopupData.ActivityLog.Preview> {
	@service('analytics') declare analyticsService: Service.Analytics;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.ACTIVITY_LOG_POPUP);
	}

	get title() {
		if (isPopupDataForMetricRegistry(this.popupData)) {
			return this.popupData.metricRegistryItem.name;
		}

		const model = this.popupData.model;
		if (
			model instanceof Board ||
			model instanceof BoardLoop ||
			model instanceof Alert ||
			model instanceof Team
		) {
			return model.name;
		} else if (model instanceof CustomQuery) {
			return model.queryName;
		} else if (model instanceof Script) {
			return model.description;
		} else if (model instanceof BoardShare) {
			return model.boardObject?.name || model.loopObject?.name;
		} else if (model instanceof HorizonMetric) {
			return model.metricName;
		} else {
			return model.title;
		}
	}

	get objectId() {
		return isPopupDataForMetricRegistry(this.popupData)
			? undefined
			: this.popupData.model.numericId!;
	}

	// The id alone matches every object in the space with that id, so the log is pinned to the types
	// this model's events are logged as.
	get objectTypes(): ActivityLogObjectType[] | undefined {
		if (isPopupDataForMetricRegistry(this.popupData)) {
			return undefined;
		}

		const model = this.popupData.model;
		if (this.popupData.objectTypes) {
			return this.popupData.objectTypes;
		} else if (model instanceof Board) {
			return [ActivityLogObjectType.BOARD];
		} else if (model instanceof BoardLoop) {
			return [ActivityLogObjectType.BOARD_LOOP];
		} else if (model instanceof Report) {
			return [ActivityLogObjectType.REPORT];
		} else if (model instanceof Goal) {
			return [ActivityLogObjectType.GOAL];
		} else if (model instanceof Team) {
			return [ActivityLogObjectType.TEAMS];
		} else if (model instanceof Source) {
			// a source's verification, and a dataset's (also a source) own events, are logged as DATASET
			return [ActivityLogObjectType.DATA_SOURCE, ActivityLogObjectType.DATASET];
		} else if (model instanceof CustomQuery || model instanceof HorizonMetric) {
			// a horizon metric logs custom query events; a custom metric's tag changes are UNDEFINED
			return [ActivityLogObjectType.CUSTOM_QUERY, ActivityLogObjectType.UNDEFINED];
		} else if (model instanceof Script) {
			return [ActivityLogObjectType.CALCULATED_METRIC, ActivityLogObjectType.UNDEFINED];
		} else {
			return undefined;
		}
	}

	get path() {
		return isPopupDataForMetricRegistry(this.popupData)
			? `/metric-registry/${this.popupData.metricRegistryItem.id}/activity-logs`
			: undefined;
	}

	get modelTypeName() {
		return isPopupDataForMetricRegistry(this.popupData)
			? 'metric'
			: getPrettyModelName(this.popupData.model);
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="ActivityLog" @size="lg" />Activity log for '{{this.title}}'
			{{this.modelTypeName}}
		</div>
		<div class="content" {{visibleCheckContainer true}}>
			<AccountLogsComponent
				@objectId={{this.objectId}}
				@objectTypes={{this.objectTypes}}
				@path={{this.path}}
				@onRequestFinished={{this.scheduleReposition}}
			/>

		</div>
	</template>
}
